import {
    Avatar,
    Box,
    Button,
    Collapse,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography
} from "@mui/material";
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import React, { useState } from "react";
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { ManipulationTypes } from "../../enums/ManipulationTypes";
import { TempoTypes } from "../../enums/TempoTypes";
import { RTL } from "../../cache/CacheRtl";
import TimerIcon from '@mui/icons-material/Timer';
import { isCardioExercise, isCatalystExercise } from "../../utils/exerciseUtils";
import { imageUrl } from "../../utils/imageUrl";

const ExerciseItem = React.memo(({ exercise, onRemove, onExerciseUpdate, exerciseNumber, readOnly }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleDeleteClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const idToRemove = exercise.exerciseId || exercise.id;
        onRemove(idToRemove);
    };

    const handleExerciseClick = (e) => {
        if (e.target.closest('.delete-btn') || e.target.closest('.MuiTextField-root') || e.target.closest('.MuiSelect-root')) {
            return;
        }
        setIsExpanded(!isExpanded);
    };

    const handleConfigChange = (field, value) => {
        const fieldMapping = {
            reps: 'recommendedReps',
            manipulation: 'manipulation',
            tempo: 'tempo',
            restTime: 'restTimeInSec',
            notes: 'comment',
            numberOfSets: 'numberOfSets',
            durationTime: 'durationTime',
        };
        const exerciseProperty = fieldMapping[field];
        const updatedExercise = { ...exercise, [exerciseProperty]: value };
        if (onExerciseUpdate) {
            onExerciseUpdate(updatedExercise);
        }
    };

    const handleAddSetRange = () => {
        const setRanges = exercise.setRanges || [];
        const newSetRange = { setIndex: setRanges.length, minReps: null, maxReps: null };
        if (onExerciseUpdate) {
            onExerciseUpdate({ ...exercise, setRanges: [...setRanges, newSetRange] });
        }
    };

    const handleRemoveSetRange = (indexToRemove) => {
        const setRanges = exercise.setRanges || [];
        const updatedRanges = setRanges
            .filter((_, idx) => idx !== indexToRemove)
            .map((range, idx) => ({ ...range, setIndex: idx }));
        if (onExerciseUpdate) {
            onExerciseUpdate({ ...exercise, setRanges: updatedRanges.length > 0 ? updatedRanges : undefined });
        }
    };

    const handleSetRangeChange = (setIndex, field, value) => {
        const setRanges = exercise.setRanges || [];
        const updatedRanges = setRanges.map((range, idx) =>
            idx === setIndex ? { ...range, [field]: value } : range
        );
        if (onExerciseUpdate) {
            onExerciseUpdate({ ...exercise, setRanges: updatedRanges });
        }
    };

    const displayName = exercise.exerciseName || exercise.name || exercise.nameHeb || 'Unknown Exercise';
    const isCardio = isCardioExercise(exercise);
    const isCatalyst = isCatalystExercise(exercise);
    const sets = exercise.numberOfSets || 3;
    const reps = exercise.recommendedReps || 10;
    const manipulationRaw = exercise.manipulation || 'RP_NEW';
    const manipulationLabel = ManipulationTypes.find(m => m.value === manipulationRaw)?.label || manipulationRaw;
    const durationMinutes = exercise.durationTime || 60;
    const catalystMinDuration = exercise.defaultMinDuration || 30;
    const catalystMaxDuration = exercise.defaultMaxDuration || 60;
    // Optional — null/undefined means the trainer left it blank.
    const catalystRecommendedDuration = exercise.recommendedDuration ?? null;
    const recommendedOutOfRange = catalystRecommendedDuration != null
        && (catalystRecommendedDuration < catalystMinDuration || catalystRecommendedDuration > catalystMaxDuration);

    const formatDuration = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    return (
        <Paper
            elevation={1}
            sx={{
                backgroundColor: '#ffffff',
                borderRight: '4px solid',
                borderRightColor: isCardio ? '#E91E63' : isCatalyst ? '#FF6B00' : 'primary.main',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    elevation: 3,
                    transform: 'translateY(-1px)',
                    '& .delete-btn': { opacity: 1 },
                    '& .expand-icon': { opacity: 1 }
                }
            }}
        >
            {/* Header - always visible */}
            <Box
                onClick={handleExerciseClick}
                sx={{
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    {/* Exercise image */}
                    <Avatar
                        src={imageUrl(exercise.imageUrl || exercise.coverUrl, 36)}
                        variant="rounded"
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '8px',
                            backgroundColor: '#F1F5F9',
                            flexShrink: 0,
                        }}
                    >
                        {isCardio
                            ? <DirectionsRunIcon sx={{ color: '#E91E63', fontSize: 18 }} />
                            : isCatalyst
                            ? <TimerIcon sx={{ color: '#FF6B00', fontSize: 18 }} />
                            : <FitnessCenterIcon sx={{ color: '#CBD5E1', fontSize: 18 }} />
                        }
                    </Avatar>

                    {/* Name and config summary */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: '#1E293B',
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                            {displayName}
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.72rem',
                            color: '#94A3B8',
                            lineHeight: 1.3,
                            mt: 0.25,
                        }}>
                            {isCardio
                                ? `${durationMinutes} דק׳`
                                : isCatalyst
                                ? `${sets} סטים · ${formatDuration(catalystMinDuration)}-${formatDuration(catalystMaxDuration)}${catalystRecommendedDuration != null ? ` · מומלץ ${formatDuration(catalystRecommendedDuration)}` : ''} · ${manipulationLabel}`
                                : `${sets} סטים · ${reps} חזרות · ${manipulationLabel}`
                            }
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    {!readOnly && (
                        <IconButton
                            onClick={handleDeleteClick}
                            size="small"
                            sx={{
                                color: '#94A3B8',
                                p: 0.5,
                                '&:hover': { color: 'error.main' },
                            }}
                        >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                    <IconButton
                        className="expand-icon"
                        sx={{
                            width: 28,
                            height: 28,
                            color: isCardio ? '#E91E63' : isCatalyst ? '#FF6B00' : 'primary.main',
                            opacity: 0.6,
                            transition: 'all 0.2s ease-in-out',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                        size="small"
                    >
                        <ExpandMoreOutlinedIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Expandable config */}
            <Collapse in={isExpanded}>
                <Divider />
                <Box sx={{
                    p: 2,
                    backgroundColor: '#FAFBFC',
                    direction: 'rtl',
                    ...(readOnly && { opacity: 0.7, pointerEvents: 'none' }),
                }}>
                    {isCardio ? (
                        /* Cardio config: Duration + Notes only */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {/* Duration in minutes */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                    משך (דקות)
                                </Typography>
                                <TextField
                                    type="number"
                                    value={exercise.durationTime || 60}
                                    onChange={(e) => handleConfigChange('durationTime', parseInt(e.target.value) || 0)}
                                    size="small"
                                    fullWidth
                                    inputProps={{ min: 1, max: 999 }}
                                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                />
                            </Box>

                            {/* Notes */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                    הערות
                                </Typography>
                                <TextField
                                    multiline
                                    rows={2}
                                    value={exercise.comment || ""}
                                    onChange={(e) => handleConfigChange('notes', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 },
                                        '& .MuiInputBase-input': { textAlign: 'right', direction: 'rtl' }
                                    }}
                                    placeholder="הערות נוספות על התרגיל..."
                                />
                            </Box>

                            {/* Delete button */}
                            {!readOnly && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                    <RTL>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteOutlineIcon />}
                                            onClick={handleDeleteClick}
                                            className="delete-btn"
                                            sx={{
                                                borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3,
                                                borderColor: 'error.main', color: 'error.main',
                                                '&:hover': { backgroundColor: 'error.main', color: 'white', borderColor: 'error.main' }
                                            }}
                                        >
                                            מחק תרגיל
                                        </Button>
                                    </RTL>
                                </Box>
                            )}
                        </Box>
                    ) : isCatalyst ? (
                        /* Catalyst config: Sets + Duration range + Manipulation + Rest + Notes */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {/* Row 1: Sets and Rest Time */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        מספר סטים
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={exercise.numberOfSets || 3}
                                        onChange={(e) => handleConfigChange('numberOfSets', parseInt(e.target.value) || 1)}
                                        size="small" fullWidth
                                        inputProps={{ min: 1, max: 20 }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        זמן מנוחה (שניות)
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={exercise.restTimeInSec || 120}
                                        onChange={(e) => handleConfigChange('restTime', parseInt(e.target.value) || 0)}
                                        size="small" fullWidth
                                        inputProps={{ min: 0, max: 9999 }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                    />
                                </Box>
                            </Box>

                            {/* Row 2: Duration range + optional recommended time (seconds) */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        משך מינימום (שניות)
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={exercise.defaultMinDuration || 30}
                                        onChange={(e) => {
                                            const updated = { ...exercise, defaultMinDuration: parseInt(e.target.value) || 0 };
                                            if (onExerciseUpdate) onExerciseUpdate(updated);
                                        }}
                                        size="small" fullWidth
                                        inputProps={{ min: 1, max: 9999 }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        משך מקסימום (שניות)
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={exercise.defaultMaxDuration || 60}
                                        onChange={(e) => {
                                            const updated = { ...exercise, defaultMaxDuration: parseInt(e.target.value) || 0 };
                                            if (onExerciseUpdate) onExerciseUpdate(updated);
                                        }}
                                        size="small" fullWidth
                                        inputProps={{ min: 1, max: 9999 }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        זמן מומלץ (שניות)
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={catalystRecommendedDuration ?? ''}
                                        onChange={(e) => {
                                            const parsed = parseInt(e.target.value);
                                            const updated = { ...exercise, recommendedDuration: Number.isNaN(parsed) ? null : parsed };
                                            if (onExerciseUpdate) onExerciseUpdate(updated);
                                        }}
                                        placeholder="ללא"
                                        error={recommendedOutOfRange}
                                        helperText={recommendedOutOfRange ? 'מחוץ לטווח המשך' : undefined}
                                        size="small" fullWidth
                                        inputProps={{ min: 1, max: 9999 }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                    />
                                </Box>
                            </Box>

                            {/* Row 3: Manipulation */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                    מניפולציה
                                </Typography>
                                <RTL>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={exercise.manipulation || "RP_NEW"}
                                            onChange={(e) => handleConfigChange('manipulation', e.target.value)}
                                            sx={{ backgroundColor: 'white', borderRadius: 1.5 }}
                                        >
                                            {ManipulationTypes.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Box>

                            {/* Row 4: Notes */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                    הערות
                                </Typography>
                                <TextField
                                    multiline rows={2}
                                    value={exercise.comment || ""}
                                    onChange={(e) => handleConfigChange('notes', e.target.value)}
                                    size="small" fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 },
                                        '& .MuiInputBase-input': { textAlign: 'right', direction: 'rtl' }
                                    }}
                                    placeholder="הערות נוספות על התרגיל..."
                                />
                            </Box>

                            {/* Delete button */}
                            {!readOnly && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                    <RTL>
                                        <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />}
                                            onClick={handleDeleteClick} className="delete-btn"
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3,
                                                borderColor: 'error.main', color: 'error.main',
                                                '&:hover': { backgroundColor: 'error.main', color: 'white', borderColor: 'error.main' }
                                            }}>
                                            מחק תרגיל
                                        </Button>
                                    </RTL>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        /* Strength config: full editing UI */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {/* Row 1: Reps and Sets */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        חזרות
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={exercise.recommendedReps || 10}
                                        onChange={(e) => handleConfigChange('reps', parseInt(e.target.value) || 0)}
                                        size="small"
                                        fullWidth
                                        inputProps={{ min: 1, max: 999 }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        מספר סטים
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={exercise.numberOfSets || 3}
                                        onChange={(e) => handleConfigChange('numberOfSets', parseInt(e.target.value) || 1)}
                                        size="small"
                                        fullWidth
                                        inputProps={{ min: 1, max: 20 }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                    />
                                </Box>
                            </Box>

                            {/* Row 2: Manipulation and Tempo */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        מניפולציה
                                    </Typography>
                                    <RTL>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={exercise.manipulation || "RP_NEW"}
                                                onChange={(e) => handleConfigChange('manipulation', e.target.value)}
                                                sx={{ backgroundColor: 'white', borderRadius: 1.5 }}
                                            >
                                                {ManipulationTypes.map((type) => (
                                                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </RTL>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                        קצב תנועה
                                    </Typography>
                                    <RTL>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={exercise.tempo || "T1_0_1_0"}
                                                onChange={(e) => handleConfigChange('tempo', e.target.value)}
                                                sx={{ backgroundColor: 'white', borderRadius: 1.5 }}
                                            >
                                                {TempoTypes.map((tempo) => (
                                                    <MenuItem key={tempo.value} value={tempo.value}>{tempo.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </RTL>
                                </Box>
                            </Box>

                            {/* Row 3: Rest Time */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                    זמן מנוחה (שניות)
                                </Typography>
                                <TextField
                                    type="number"
                                    value={exercise.restTimeInSec || 120}
                                    onChange={(e) => handleConfigChange('restTime', parseInt(e.target.value) || 0)}
                                    size="small"
                                    fullWidth
                                    inputProps={{ min: 0, max: 9999 }}
                                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                />
                            </Box>

                            {/* Row 4: Notes */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, textAlign: 'right', color: 'text.secondary' }}>
                                    הערות
                                </Typography>
                                <TextField
                                    multiline
                                    rows={2}
                                    value={exercise.comment || ""}
                                    onChange={(e) => handleConfigChange('notes', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 },
                                        '& .MuiInputBase-input': { textAlign: 'right', direction: 'rtl' }
                                    }}
                                    placeholder="הערות נוספות על התרגיל..."
                                />
                            </Box>

                            {/* Row 5: Per-Set Rep Ranges */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                        טווח חזרות לפי סט
                                    </Typography>
                                    <IconButton onClick={handleAddSetRange} size="small" sx={{ color: 'primary.main' }}>
                                        <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                {exercise.setRanges && exercise.setRanges.length > 0 && (
                                    <Box sx={{ backgroundColor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                                        <Box sx={{
                                            display: 'grid', gridTemplateColumns: '32px 1fr 1fr 32px', gap: 0,
                                            alignItems: 'center', p: '6px 8px', borderBottom: '1px solid #E2E8F0', backgroundColor: '#F1F5F9'
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.secondary' }}>סט</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.secondary' }}>מינימום</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.secondary' }}>מקסימום</Typography>
                                            <Box />
                                        </Box>
                                        {exercise.setRanges.map((setRange, idx) => (
                                            <Box key={idx} sx={{
                                                display: 'grid', gridTemplateColumns: '32px 1fr 1fr 32px', gap: 0,
                                                alignItems: 'center', p: '4px 8px',
                                                borderBottom: idx < exercise.setRanges.length - 1 ? '1px solid #E2E8F0' : 'none',
                                                '&:hover': { backgroundColor: '#F1F5F9' }
                                            }}>
                                                <Box sx={{
                                                    width: 22, height: 22, borderRadius: '50%', backgroundColor: 'primary.main',
                                                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '0.7rem', fontWeight: 700, mx: 'auto'
                                                }}>
                                                    {idx + 1}
                                                </Box>
                                                <Box sx={{ px: 0.5 }}>
                                                    <TextField
                                                        type="number" value={setRange.minReps || ''} size="small" placeholder="6"
                                                        onChange={(e) => handleSetRangeChange(idx, 'minReps', e.target.value ? parseInt(e.target.value) : null)}
                                                        inputProps={{ min: 1, max: 999, style: { textAlign: 'center', padding: '4px 6px' } }}
                                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1 } }}
                                                    />
                                                </Box>
                                                <Box sx={{ px: 0.5 }}>
                                                    <TextField
                                                        type="number" value={setRange.maxReps || ''} size="small" placeholder="12"
                                                        onChange={(e) => handleSetRangeChange(idx, 'maxReps', e.target.value ? parseInt(e.target.value) : null)}
                                                        inputProps={{ min: 1, max: 999, style: { textAlign: 'center', padding: '4px 6px' } }}
                                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1 } }}
                                                    />
                                                </Box>
                                                <IconButton onClick={() => handleRemoveSetRange(idx)} size="small" sx={{ color: '#94A3B8', p: '2px', '&:hover': { color: 'error.main' } }}>
                                                    <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* Delete button - hidden when readOnly */}
                            {!readOnly && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                    <RTL>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteOutlineIcon />}
                                            onClick={handleDeleteClick}
                                            className="delete-btn"
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                px: 3,
                                                borderColor: 'error.main',
                                                color: 'error.main',
                                                '&:hover': {
                                                    backgroundColor: 'error.main',
                                                    color: 'white',
                                                    borderColor: 'error.main',
                                                }
                                            }}
                                        >
                                            מחק תרגיל
                                        </Button>
                                    </RTL>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
});

export default ExerciseItem;
